import React, { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import SearchBox from "../../../components/ACS-Dashboard/ACS-Blocks/SearchBox";
import GlobalPagination from "../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import { MarketTag, RateOnboardChip } from "./ui";

const HEADERS = [
  { key: "lane", label: "Lane" },
  { key: "contracted", label: "Contracted Rate", numeric: true },
  { key: "benchmark", label: "Market Benchmark", numeric: true },
  { key: "fuelTerms", label: "Fuel Terms" },
  { key: "validThrough", label: "Valid Through" },
  { key: "marketLabel", label: "Status" },
  { key: "rateOnboardingLabel", label: "Rate Onboarding" },
];

const SORTABLE_HEADERS = HEADERS.map((h) => h.label);
const SORT_KEY_BY_LABEL = Object.fromEntries(
  HEADERS.map((h) => [h.label, h.key])
);

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

function rowSearchText(r) {
  return [
    r.lane,
    r.detail,
    r.contracted,
    r.benchmark,
    r.fuelTerms,
    r.validThrough,
    r.marketLabel,
    r.rateOnboardingLabel,
  ]
    .filter((v) => v != null && v !== "")
    .join(" ")
    .toLowerCase();
}

function compareValues(a, b, key, numeric) {
  const av = a?.[key];
  const bv = b?.[key];
  if (numeric) {
    const an = Number(av);
    const bn = Number(bv);
    if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
  }
  const as = av == null || av === "—" ? "" : String(av).toLowerCase();
  const bs = bv == null || bv === "—" ? "" : String(bv).toLowerCase();
  return as.localeCompare(bs, undefined, { numeric: true, sensitivity: "base" });
}

function SortIcon({ active, ascending }) {
  if (!active) {
    return <ArrowUpDown size={12} className="opacity-40" />;
  }
  return ascending ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
}

/**
 * Client-side search / sort / pagination for carrier lane rates.
 * Default page size is 5 (lane lists can grow large after rate extraction).
 */
export default function LaneRatesGrid({ rates = [] }) {
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [sortBy, setSortBy] = useState("lane");
  const [ascending, setAscending] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  useEffect(() => {
    setCurrentPage(1);
  }, [rates, appliedSearch, sortBy, ascending, itemsPerPage]);

  const filteredSorted = useMemo(() => {
    const needle = appliedSearch.trim().toLowerCase();
    let rows = rates;
    if (needle) {
      rows = rows.filter((r) => rowSearchText(r).includes(needle));
    }
    const header = HEADERS.find((h) => h.key === sortBy) || HEADERS[0];
    const sorted = [...rows].sort((a, b) =>
      compareValues(a, b, header.key, header.numeric)
    );
    if (!ascending) sorted.reverse();
    return sorted;
  }, [rates, appliedSearch, sortBy, ascending]);

  const totalCount = filteredSorted.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const safePage = Math.min(currentPage, totalPages);
  const pageRows = filteredSorted.slice(
    (safePage - 1) * itemsPerPage,
    safePage * itemsPerPage
  );

  const handleSort = (label) => {
    const key = SORT_KEY_BY_LABEL[label];
    if (!key) return;
    if (sortBy === key) {
      setAscending((prev) => !prev);
    } else {
      setSortBy(key);
      setAscending(true);
    }
  };

  const runSearch = () => {
    setAppliedSearch(searchInput);
    setCurrentPage(1);
  };

  return (
    <div className="overflow-hidden rounded-md border border-gray-300 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <SearchBox
            value={searchInput}
            onChange={setSearchInput}
            onSearch={runSearch}
            placeholder="Search lanes / rates"
          />
          <span className="text-dense-xs font-semibold text-gray-500">
            {totalCount} lane{totalCount === 1 ? "" : "s"}
            {appliedSearch.trim() && totalCount !== rates.length
              ? ` of ${rates.length}`
              : ""}
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse text-fluid-sm">
          <thead>
            <tr className="bg-gray-100">
              {HEADERS.map((h) => {
                const sortable = SORTABLE_HEADERS.includes(h.label);
                const active = sortBy === h.key;
                return (
                  <th
                    key={h.key}
                    className={`border-b border-gray-200 px-3 py-2 text-left text-dense-xs font-semibold text-gray-600 ${
                      sortable
                        ? "cursor-pointer select-none hover:text-[#223688]"
                        : ""
                    }`}
                    onClick={() => sortable && handleSort(h.label)}
                    aria-sort={
                      active ? (ascending ? "ascending" : "descending") : "none"
                    }
                  >
                    <span className="inline-flex items-center gap-1">
                      {h.label}
                      {sortable ? (
                        <SortIcon active={active} ascending={ascending} />
                      ) : null}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr key={r.id} className="border-b border-gray-200 last:border-0">
                <td className="px-3 py-2">
                  <div className="font-semibold">{r.lane}</div>
                  <div className="text-dense-xs text-gray-500">{r.detail}</div>
                </td>
                <td className="px-3 py-2 font-mono font-semibold">
                  ${Number(r.contracted).toLocaleString()}
                </td>
                <td className="px-3 py-2">
                  ${Number(r.benchmark).toLocaleString()}
                </td>
                <td className="px-3 py-2">{r.fuelTerms}</td>
                <td className="px-3 py-2 font-mono">{r.validThrough}</td>
                <td className="px-3 py-2">
                  <MarketTag tag={r.marketTag} label={r.marketLabel} />
                </td>
                <td className="px-3 py-2">
                  <RateOnboardChip
                    status={r.rateOnboarding}
                    label={r.rateOnboardingLabel}
                  />
                </td>
              </tr>
            ))}
            {!rates.length ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  No lane rates yet.
                </td>
              </tr>
            ) : !pageRows.length ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  No lanes match your search.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {rates.length > 0 ? (
        <GlobalPagination
          label="Lanes per page"
          currentPage={safePage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={(n) => {
            setItemsPerPage(n);
            setCurrentPage(1);
          }}
          options={PAGE_SIZE_OPTIONS}
        />
      ) : null}
    </div>
  );
}
